import {useMutation, useQueryClient} from "@tanstack/react-query";
import {calculateRefundAmount, confirmCancelBooking} from "api/booking.api";
import {useToast} from "hooks";
import {useState} from "react";
import styled from "styled-components";
import {RequestedClassCard} from "ui/base/class/RequestedClassCard";
import {UpcomingClassCard} from "ui/base/class/UpcomingClassCard";
import {convertHexToRGBA, defaultThemeColors, fonts, mediaSizes} from "ui/theme";
import {CancellationModal} from "./CancellationModal";
import {hideScrollBar} from "ui/base/scrollbarStyle/scrollbarStyle";
import {useNavigate} from "react-router-dom";
import {userRoute} from "../../user.routes";
import {PrevButton} from "ui/base/Button/EmblaButtons";
import {NoContent} from "ui/base/NoContent/NoContent";
import { LoaderComponent } from "ui/base/loading/LoaderComponent";
import NoClassRequestFound from "ui/icons/not-found-icons/noRequest.svg";
import {CancelClassModal} from "ui/blocks/CancelClassModal/CancelClassModal";

export const UpcomingClasses = ({
                                    futureClasses,
                                    pendingClasses,
                                    setTabToNull,
                                    isFutureClassesLoading,
                                    isPendingClassesLoading
                                }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState("1");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancellationData, setCancellationData] = useState(null);
    const [bookingIdToCancel, setBookingIdToCancel] = useState(null);
    const [selectedBooking, setSelectedBooking] = useState(null);

    const {mutate: getRefundAmount, isLoading} = useMutation({
        mutationFn: calculateRefundAmount,
        onError: () => useToast("error", "Refund data can not load"),
        onSuccess: (data) => {
            setCancellationData(data);
            setShowCancelModal(true);
        },
    });

    const {mutate: cancelBooking, isLoading: cancelLoading} = useMutation({
        mutationFn: confirmCancelBooking,
        onError: () => useToast("error", "Cancellation failed"),
        onSuccess: () => {
            useToast("success", "Your class has been cancelled");
            queryClient.invalidateQueries(["getFutureClassesForUser2"]);
            queryClient.invalidateQueries(["getPendingClassesForUser"]);
            setShowCancelModal(false);
            setCancellationData(null);
            setBookingIdToCancel(null);
        },
    });

    const onCancelUpcoming = (classData) => {
        if (classData?.booking_id) {
            getRefundAmount({
                booking_id: classData?.booking_id,
                type: 1,
            });
        } else {
            useToast("error", "Booking id does not exist");
        }
    };

    // Function to set the booking id and display the cancel class modal
    const onCancelRequested = (pendingClasses) => {
        const bookingId = Array(pendingClasses?.booking_slots) &&
        pendingClasses?.booking_slots?.length > 0
            ? pendingClasses?.booking_slots[0]?.booking_id
            : undefined

        if (bookingId) {
            setBookingIdToCancel(bookingId)
        } else {
            useToast("error", "Booking id does not exist")
        }
    };

    return (
        <>
            {cancellationData ? (
                <CancellationModal
                    show={showCancelModal}
                    handleClose={() => setShowCancelModal(false)}
                    cancellationData={cancellationData}
                    isLoading={cancelLoading}
                    onCancel={() => {
                        cancelBooking({
                            booking_id:
                                Array(cancellationData?.booking_slots) &&
                                cancellationData?.booking_slots?.length > 0
                                    ? cancellationData?.booking_slots[0]?.booking_id
                                    : undefined,
                            type: 1,
                        })
                        setSelectedBooking(null);
                    }
                    }
                    onClose={() => {
                        setSelectedBooking(null);
                        setShowCancelModal(false);
                        setCancellationData(null);
                    }}
                />
            ) : null}

            <CancelClassModal
                show={!!bookingIdToCancel}
                bookingId={bookingIdToCancel}
                type={1}
                handleClose={(() => setBookingIdToCancel(null))}
                handleCancelClass={() => {
                    cancelBooking({
                        booking_id: bookingIdToCancel,
                        type: 1,
                    })
                }}/>

            <Content>
                <HeaderTitleContainer>
                    <PrevButton onClick={setTabToNull}/>
                    <HeaderTitle> Upcoming Classes</HeaderTitle>
                </HeaderTitleContainer>
                <TabHeader>
                    <TabButton
                        active={activeTab === "1"}
                        onClick={() => setActiveTab("1")}
                    >
                        Upcoming Classes
                    </TabButton>
                    <TabButton
                        active={activeTab === "2"}
                        onClick={() => setActiveTab("2")}
                    >
                        Requested Classes{" "}
                        {pendingClasses?.length ? (
                            <Badge>{pendingClasses?.length || 0}</Badge>
                        ) : null}
                    </TabButton>
                </TabHeader>
                <ClassList isLoading={isFutureClassesLoading || isPendingClassesLoading}>
                    {activeTab === "1"
                        ? isFutureClassesLoading ? (
                            <LoadingContainer>
                                <LoaderComponent minHeight="auto" />
                            </LoadingContainer>
                        ) : (
                            futureClasses.length
                                ? futureClasses?.map((futureClasse) => (
                                    <UpcomingClassCard
                                        key={futureClasse.id}
                                        upcomingClass={futureClasse}
                                        sendMessage={() => navigate(userRoute.messages, {
                                            state: {
                                                classDetail: futureClasse
                                            }
                                        })}
                                        onCancel={() => {
                                            onCancelUpcoming(futureClasse)
                                            setSelectedBooking(futureClasse);
                                        }}
                                        isLoading={isLoading || cancelLoading}
                                    />
                                ))
                                : <div style={{gridColumn: "1 / -1", paddingTop: '2rem'}}>
                                    <NoContent
                                        title='No upcoming classes found'
                                        image={NoClassRequestFound}
                                        size={280}/>
                                </div>
                        ) : null}

                    {activeTab === "2"
                        ? isPendingClassesLoading ? (
                            <LoadingContainer>
                                <LoaderComponent minHeight="auto" />
                            </LoadingContainer>
                        ) : (
                            pendingClasses.length ?
                                pendingClasses?.map((pendingClasses) => (
                                    <RequestedClassCard
                                        key={pendingClasses.id}
                                        requestedClass={pendingClasses}
                                        isLoading={cancelLoading}
                                        sendMessage={() => navigate(userRoute.messages, {
                                            state: {
                                                classDetail: pendingClasses
                                            }
                                        })}
                                        onCancel={() => onCancelRequested(pendingClasses)}
                                    />
                                )) : <div style={{gridColumn: "1 / -1", paddingTop: '2rem'}}>
                                    <NoContent
                                        title='No class request found'
                                        image={NoClassRequestFound}
                                        size={280}/>
                                </div>
                        ) : null}
                </ClassList>
            </Content>
        </>
    );
};

const Content = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
`;

const TabHeader = styled.div`
    display: flex;
    width: 100%;
`;

const TabButton = styled.button`
    background-color: transparent;
    width: 50%;
    border: none;
    padding: 0.5rem 0.5rem;
    cursor: pointer;
    font-size: ${fonts.sizes.extraSmall};
    border-bottom: 2.5px solid ${({active}) =>
            active
                    ? defaultThemeColors.blue
                    : convertHexToRGBA(defaultThemeColors.blue, 0.1)};
    color: ${({active}) =>
            active ? defaultThemeColors.blue : defaultThemeColors.txt};
    font-weight: 600;
    @media (width >= ${mediaSizes.lg}) {
        padding: 0.5rem 1rem;
        font-size: ${fonts.sizes.regular};
    }
`;

const Badge = styled.span`
    background: ${defaultThemeColors.blue};
    color: ${defaultThemeColors.white};
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 12px;
    margin-right: auto;
`;

const ClassList = styled.div`
    ${({isLoading}) => !isLoading ? `
        display: grid;
        gap: 0.8rem;
        overflow: scroll;

        ${hideScrollBar};
        grid-template-columns:1fr;
        @media (width >= ${mediaSizes.lg}) {
            grid-template-columns: repeat(2, 1fr);
        }
    ` : `
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    `}
`;

const HeaderTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1.2rem;
    margin-bottom: 1.8rem;
    @media (width >= ${mediaSizes.md}) {
        display: none;
    }
`

const HeaderTitle = styled.div`
    font-size: ${fonts.sizes.big};
    font-weight: 600;

    @media (width >= ${mediaSizes.md}) {
        font-size: ${fonts.sizes.h4};
    }
    @media (width >= ${mediaSizes.lg}) {
        font-size: ${fonts.sizes["h3.4"]};
    }
`

const LoadingContainer = styled.div`
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
    align-content: center;
    text-align: center;
`;

